import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Loading, Avatar, StatTile, StreakBadge } from "../components/common";
import { gameLabel } from "./Dashboard";
import { ArrowLeft } from "lucide-react";

export default function PlayerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setP((await api.get(`/players/${id}`)).data);
      } catch {
        navigate("/players");
      }
    })();
  }, [id, navigate]);

  if (!p) return <Loading label="Loading profile" />;
  const o = p.overall;
  const d = p.detail_501;

  return (
    <ProfileBody p={p} o={o} d={d} navigate={navigate} />
  );
}

function ProfileBody({ p, o, d, navigate }) {
  return (
    <div className="space-y-8">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm font-bold uppercase text-slate-400 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Hero record */}
      <div className="flex flex-col md:flex-row md:items-center gap-6">
        <Avatar name={p.name} size={96} />
        <div>
          <h1 className="font-display text-6xl md:text-7xl font-black uppercase tracking-tighter text-white leading-none">
            {p.name}
          </h1>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mt-2">
            All Games · Match Record
          </div>
          <div className="flex items-center gap-4 mt-1">
            <span data-testid="profile-record" className="font-display text-4xl font-black text-white">
              {o.matches_won} <span className="text-slate-600">-</span> {o.matches_lost}
            </span>
            <span className="font-display text-2xl font-bold text-[#FFCC00]">{o.win_pct}% WIN RATE</span>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            A win = 1 per match (single game or best-of series). Legs {o.legs_won}-{o.legs_lost} shown as
            detail.
          </div>
        </div>
      </div>

      {/* Career overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
        <StatTile testid="stat-matches" label="Matches" value={o.matches_played} />
        <StatTile testid="stat-legs" label="Legs Won-Lost" value={`${o.legs_won}-${o.legs_lost}`} />
        <StatTile
          testid="stat-current-streak"
          label="Current Streak"
          value={<StreakBadge streak={o.current_streak} />}
        />
        <StatTile
          testid="stat-longest-streak"
          label="Longest Win Streak"
          value={<span className="text-[#FFCC00]">W{o.longest_win_streak}</span>}
        />
      </div>

      {/* Per game records */}
      <div className="grid gap-1 md:grid-cols-3">
        <GameRecordCard title="501 Record" rec={p.record_501} />
        <GameRecordCard title="Around the World" rec={p.record_atw} />
        <GameRecordCard title="Baseball" rec={p.record_baseball} />
      </div>

      {/* 501 career stats */}
      {p.record_501.matches_played > 0 && (
        <div>
          <h2 className="font-display text-2xl font-bold uppercase text-white mb-3">501 Career Stats</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
            <StatTile testid="d-avg" label="3-Dart Average" value={d.three_dart_avg} accent="text-[#007AFF]" />
            <StatTile testid="d-first9" label="First 9 Avg" value={d.first9_avg} />
            <StatTile testid="d-highavg" label="Highest Match Avg" value={d.highest_match_avg} />
            <StatTile testid="d-highturn" label="Highest Score" value={d.highest_turn} />
            <StatTile testid="d-100" label="100+" value={d.count_100} />
            <StatTile testid="d-140" label="140+" value={d.count_140} />
            <StatTile testid="d-180" label="180s" value={d.count_180} accent="text-[#FF3B30]" />
            <StatTile testid="d-hco" label="Highest Checkout" value={d.highest_checkout} accent="text-[#FFCC00]" />
            <StatTile testid="d-copct" label="Checkout %" value={`${d.checkout_pct}%`} />
            <StatTile testid="d-bestleg" label="Best Leg (darts)" value={d.best_winning_leg || "—"} />
            <StatTile testid="d-avgleg" label="Avg Darts/Win Leg" value={d.avg_darts_winning_leg} />
            <StatTile testid="d-totaldarts" label="Total Darts" value={d.total_darts} />
          </div>
        </div>
      )}

      {/* Around the World career stats */}
      {p.record_atw.matches_played > 0 && (
        <div>
          <h2 className="font-display text-2xl font-bold uppercase text-white mb-3">
            Around the World Stats
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
            <StatTile testid="atw-hitpct" label="Hit %" value={`${p.detail_atw.hit_pct}%`} accent="text-[#34C759]" />
            <StatTile testid="atw-darts" label="Darts Thrown" value={p.detail_atw.total_darts} />
            <StatTile testid="atw-hits" label="Targets Hit" value={p.detail_atw.total_hits} />
            <StatTile testid="atw-dpt" label="Darts / Target" value={p.detail_atw.avg_darts_per_target} />
            <StatTile
              testid="atw-fastest"
              label="Fastest Round"
              value={p.detail_atw.fastest_round_ms ? `${(p.detail_atw.fastest_round_ms / 1000).toFixed(1)}s` : "—"}
              accent="text-[#FFCC00]"
            />
          </div>
        </div>
      )}

      {/* Baseball career stats */}
      {p.record_baseball.matches_played > 0 && (
        <div>
          <h2 className="font-display text-2xl font-bold uppercase text-white mb-3">Baseball Stats</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
            <StatTile testid="bb-games" label="Games" value={p.detail_baseball.games} />
            <StatTile testid="bb-best" label="Best Score" value={p.detail_baseball.best_score} accent="text-[#FFCC00]" />
            <StatTile testid="bb-avg" label="Avg Score" value={p.detail_baseball.avg_score} accent="text-[#34C759]" />
            <StatTile testid="bb-rpi" label="Runs / Inning" value={p.detail_baseball.avg_runs_per_inning} />
          </div>
        </div>
      )}

      {/* Recent matches */}
      <div>
        <h2 className="font-display text-2xl font-bold uppercase text-white mb-3">Recent Matches</h2>
        {p.recent_matches.length === 0 ? (
          <p className="text-slate-500 text-sm">No completed matches yet.</p>
        ) : (
          <div className="space-y-1">
            {p.recent_matches.map((m) => (
              <button
                key={m.id}
                onClick={() => navigate(`/match/${m.id}`)}
                className="panel rounded-sm p-3 w-full flex items-center justify-between text-left hover:border-[#007AFF] transition-colors duration-150"
              >
                <div>
                  <span className="text-[10px] font-bold uppercase text-slate-500">
                    {gameLabel(m.game_type)} • Bo{m.best_of}
                  </span>
                  <div className="font-semibold text-white text-sm">
                    {m.player_ids.map((id2) => m.names[id2]).join(" vs ")}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-display text-xl font-bold text-white">
                    {m.player_ids.map((id2) => m.final_score[id2] ?? 0).join("-")}
                  </span>
                  <span
                    className={`font-display text-lg font-black w-8 text-center ${
                      m.won ? "text-[#34C759]" : "text-[#FF3B30]"
                    }`}
                  >
                    {m.won ? "W" : "L"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GameRecordCard({ title, rec }) {
  return (
    <div className="panel rounded-sm p-5">
      <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">{title}</div>
      {rec.matches_played === 0 ? (
        <div className="text-slate-600 font-display text-2xl font-bold">No matches</div>
      ) : (
        <div className="space-y-1">
          <div className="flex items-end gap-3">
            <div className="font-display text-4xl font-black text-white">
              {rec.matches_won}-{rec.matches_lost}
            </div>
            <div className="text-xs text-slate-400 font-semibold pb-1 uppercase tracking-wide">
              Matches · {rec.win_pct}%
            </div>
          </div>
          {rec.legs_played > 0 && (
            <div className="text-sm text-[#FFCC00] font-semibold">
              Legs {rec.legs_won}-{rec.legs_lost} · {rec.leg_win_pct}%
            </div>
          )}
        </div>
      )}
    </div>
  );
}
